import React from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useQuery } from "@apollo/client";
import config from "../../config";
import UserSettings from "./UserSettings";
import ATSSettings from "./ATSSettings";
import Partnerships from "./Partnerships";
import Subscriptions from "./Subscriptions/index";
import Communications from "./Communications";
import IndustrySettings from "./Industry";
import Projects from "./Projects";
import { GET_PROFILE } from "../../GraphQL/settings/queries";
import { useLocation, useParams } from "react-router-dom";
import { useFeatureFlagEnabled } from "posthog-js/react";


const Settings = () => {
  const { pathUrl } = useParams();
  const location = useLocation();
  const industryState = location.state as any;
  const { loading, error, data } = useQuery(GET_PROFILE);
  const showAccordionFlag = useFeatureFlagEnabled(config?.postHog?.featureFlags?.showAccordionSettings);
  const hasFeatureProject = data?.profile?.company?.settings?.features?.projects;
  const showSubscriptions = false;

  if (loading || error) return null;

  let defaultKey;
  if (industryState?.byIndustry) {
    defaultKey = "industry";
  } else if (pathUrl) {
    defaultKey = pathUrl;
  } else {
    defaultKey = "user-details";
  }

  return (
    <div className="max-w-5xl mx-auto px-lg py-2xl">
      {/* Page Header */}
      <div className="mb-2xl">
        <h1 className="font-serif text-3xl text-slate-900 dark:text-slate-100 mb-sm">
          Settings
        </h1>
        <p className="text-base text-slate-600 dark:text-slate-400">
          Manage your account preferences and integrations
        </p>
      </div>

      {/* Enhanced Accordion */}
      <Accordion type="single" defaultValue={defaultKey} collapsible className="space-y-lg">
        <AccordionItem
          value="user-details"
          className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
        >
          <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
            User Details
          </AccordionTrigger>
          <AccordionContent className="px-xl pb-xl pt-sm">
            <UserSettings profile={data.profile} />
          </AccordionContent>
        </AccordionItem>

        {showAccordionFlag && (
          <AccordionItem
            value="communications"
            className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
          >
            <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
              Communications
            </AccordionTrigger>
            <AccordionContent className="px-xl pb-xl pt-sm">
              <Communications />
            </AccordionContent>
          </AccordionItem>
        )}

        {(data?.profile?.role === "AccountHolder" || data?.profile?.role === "Admin") && (
          <>
            <AccordionItem
              value="connections"
              className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
            >
              <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
                ATS Connections
              </AccordionTrigger>
              <AccordionContent className="px-xl pb-xl pt-sm">
                <ATSSettings />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem
              value="partnerships"
              className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
            >
              <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
                Partnerships
              </AccordionTrigger>
              <AccordionContent className="px-xl pb-xl pt-sm">
                <Partnerships companyId={data?.profile?.companyId} />
              </AccordionContent>
            </AccordionItem>

            {showAccordionFlag && showSubscriptions && (
              <AccordionItem
                value="subscriptions"
                className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
              >
                <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
                  Subscriptions
                </AccordionTrigger>
                <AccordionContent className="px-xl pb-xl pt-sm">
                  <Subscriptions profile={data?.profile} />
                </AccordionContent>
              </AccordionItem>
            )}
          </>
        )}

        {showAccordionFlag && (
          <AccordionItem
            value="industry"
            className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
          >
            <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
              Industry Preferences
            </AccordionTrigger>
            <AccordionContent className="px-xl pb-xl pt-sm">
              <IndustrySettings profile={data?.profile} />
            </AccordionContent>
          </AccordionItem>
        )}

        {(data?.profile?.role === "AccountHolder" || data?.profile?.role === "Admin") && hasFeatureProject && (
          <AccordionItem
            value="projects"
            className="border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 shadow-sm transition-all duration-200 hover:shadow-md hover:border-teal-200 dark:hover:border-teal-700"
          >
            <AccordionTrigger className="font-serif text-xl py-lg px-xl hover:text-teal-600 dark:hover:text-teal-400 transition-colors">
              Projects
            </AccordionTrigger>
            <AccordionContent className="px-xl pb-xl pt-sm">
              <Projects />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  )
};

export default Settings;
